import { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { PrivateAssetImage } from "@/components/private-asset-image/private-asset-image";

type Company = Pick<components["schemas"]["Company"], "name" | "logoUrl">;

export enum CompanyLogoVariant {
  DEFAULT = "DEFAULT",
  MEDIUM = "MEDIUM",
  COMPACT = "COMPACT",
}

export interface CompanyLogoProps {
  company: Company;
  variant?: CompanyLogoVariant;
  className?: string;
}

const getLogoVariantClasses = (variant: CompanyLogoVariant): string => {
  switch (variant) {
    case CompanyLogoVariant.COMPACT:
      return "w-6 text-xs font-normal";
    case CompanyLogoVariant.MEDIUM:
      return "w-10 text-xs font-normal";
    case CompanyLogoVariant.DEFAULT:
    default:
      return "w-12 text-lg font-semibold";
  }
};

const getFallbackVariantClasses = (variant: CompanyLogoVariant): string => {
  switch (variant) {
    case CompanyLogoVariant.COMPACT:
      return "size-6 text-xs font-normal";
    case CompanyLogoVariant.MEDIUM:
      return "size-10 text-xs font-normal";
    case CompanyLogoVariant.DEFAULT:
    default:
      return "size-12 text-lg font-semibold";
  }
};

export const CompanyLogo = ({ company, variant = CompanyLogoVariant.DEFAULT, className }: CompanyLogoProps) => {
  const logoUrl = company.logoUrl;

  if (logoUrl) {
    return (
      <PrivateAssetImage
        src={logoUrl}
        alt={company.name || "Company logo"}
        className={cn("h-auto object-contain", getLogoVariantClasses(variant), className)}
      />
    );
  }

  return (
    <div
      role="img"
      aria-label={company.name || "Company logo"}
      className={cn(
        "bg-muted text-muted-foreground flex aspect-square items-center justify-center rounded-full border font-semibold uppercase",
        getFallbackVariantClasses(variant),
        className,
      )}
    >
      {company.name?.trimStart().slice(0, 1) || "?"}
    </div>
  );
};
